// packages/web/src/components/AuditView.tsx — misma lógica (filtros → opts memo, 4 agregados + filas con cursor).
// Cambios: barra de filtros con labels legibles y chips de filtros activos, tarjetas con cabecera
// uniforme, estado OK/Error con texto, filas con detalle expandible para el filtro del query.
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, X, ChevronRight } from 'lucide-react';
import {
  api,
  type AuditHourBucket,
  type AuditLatencyBucket,
  type AuditNamespaceBucket,
  type AuditQueryOpts,
  type AuditRow,
  type AuditUserBucket,
} from '../api';
import { Alert, Button, Card, INPUT_CLASS, PageHeader, StatusBadge, TABLE } from './ui';

const HOUR_PRESETS = [
  { label: '24 h', hours: 24 },
  { label: '7 días', hours: 24 * 7 },
  { label: '30 días', hours: 24 * 30 },
] as const;
const SLOW = ['500ms-1s', '1-5s', '>5s'];

export function AuditView({ onClose }: { onClose: () => void }) {
  const [hours, setHours] = useState<number>(24);
  const [dbType, setDbType] = useState<'all' | 'mongo' | 'postgres'>('all');
  const [minLatency, setMinLatency] = useState<number | ''>('');
  const [namespaceFilter, setNamespaceFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');

  const opts = useMemo<AuditQueryOpts>(() => {
    const o: AuditQueryOpts = { hours };
    if (dbType !== 'all') o.db_type = dbType;
    if (minLatency !== '' && minLatency > 0) o.min_latency_ms = minLatency;
    if (namespaceFilter.trim()) o.namespace = namespaceFilter.trim();
    if (userIdFilter.trim()) o.user_mongo_id = userIdFilter.trim();
    return o;
  }, [hours, dbType, minLatency, namespaceFilter, userIdFilter]);

  const active: { label: string; clear: () => void }[] = [
    ...(dbType !== 'all' ? [{ label: `Base: ${dbType}`, clear: () => setDbType('all') }] : []),
    ...(minLatency !== '' && minLatency > 0 ? [{ label: `≥ ${minLatency} ms`, clear: () => setMinLatency('') }] : []),
    ...(namespaceFilter.trim() ? [{ label: `Colección: ${namespaceFilter}`, clear: () => setNamespaceFilter('') }] : []),
    ...(userIdFilter.trim() ? [{ label: `Usuario: ${userIdFilter.slice(0, 8)}…`, clear: () => setUserIdFilter('') }] : []),
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader back={{ label: 'Cerrar', onClick: onClose }} title="Auditoría de consultas"
        subtitle="Cada consulta a Mongo y Postgres queda registrada con el usuario, el endpoint y el filtro usado. Retención: 90 días." />

      <section aria-label="Filtros" className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[auto_160px_160px_minmax(0,1fr)_minmax(0,1fr)] gap-3 items-end">
          <div>
            <span className="block text-sm font-medium text-slate-800">Ventana</span>
            <div role="radiogroup" aria-label="Ventana" className="mt-1.5 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 gap-1">
              {HOUR_PRESETS.map((p) => (
                <button key={p.hours} type="button" role="radio" aria-checked={hours === p.hours} onClick={() => setHours(p.hours)}
                  className={`rounded-md px-3 py-1.5 text-sm whitespace-nowrap ${hours === p.hours ? 'bg-marzano-600 text-white font-semibold' : 'text-slate-700 hover:bg-white'} focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor="au-db" className="block text-sm font-medium text-slate-800">Base</label>
            <select id="au-db" className={INPUT_CLASS} value={dbType} onChange={(e) => setDbType(e.target.value as typeof dbType)}>
              <option value="all">Todas</option><option value="mongo">Mongo</option><option value="postgres">Postgres</option>
            </select>
          </div>
          <div>
            <label htmlFor="au-lat" className="block text-sm font-medium text-slate-800">Latencia mínima (ms)</label>
            <input id="au-lat" type="number" min={0} placeholder="0" className={INPUT_CLASS + ' tabular-nums'} value={minLatency}
              onChange={(e) => setMinLatency(e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
          <div>
            <label htmlFor="au-ns" className="block text-sm font-medium text-slate-800">Colección o tabla</label>
            <input id="au-ns" type="text" placeholder="production.user · evaluation_session" className={INPUT_CLASS + ' font-mono'} value={namespaceFilter} onChange={(e) => setNamespaceFilter(e.target.value)} />
          </div>
          <div>
            <label htmlFor="au-user" className="block text-sm font-medium text-slate-800">Usuario (mongo_id)</label>
            <input id="au-user" type="text" placeholder="24 caracteres hex" className={INPUT_CLASS + ' font-mono'} value={userIdFilter} onChange={(e) => setUserIdFilter(e.target.value)} />
          </div>
        </div>
        {active.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-[13px] text-slate-600">Filtros activos:</span>
            {active.map((f) => (
              <button key={f.label} type="button" onClick={f.clear} className="inline-flex items-center gap-1 rounded-full border border-marzano-100 bg-marzano-50 px-2.5 py-1 text-[13px] text-marzano-700 hover:bg-marzano-100">
                {f.label}<X aria-label="Quitar" className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <VolumeByHourCard opts={opts} />
        <LatencyHistogramCard opts={opts} />
        <TopUsersCard opts={opts} onPickUser={setUserIdFilter} />
        <TopNamespacesCard opts={opts} onPickNamespace={setNamespaceFilter} />
      </div>

      <AuditRowsTable opts={opts} />
    </div>
  );
}

function useAgg<T>(fetcher: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetcher()
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
}

function VolumeByHourCard({ opts }: { opts: AuditQueryOpts }) {
  const { data, loading, error } = useAgg(() => api.getAuditVolumeByHour(opts), [opts]);
  const buckets: AuditHourBucket[] = data?.buckets ?? [];
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const total = buckets.reduce((a, b) => a + b.count, 0);
  const fmt = (iso: string) => new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit' });
  return (
    <Card title="Volumen por hora" actions={!loading && <span className="text-[13px] text-slate-600 tabular-nums">{total.toLocaleString('es-CL')} consultas</span>}>
      <Body loading={loading} error={error} empty={buckets.length === 0}>
        <div className="flex items-end gap-px h-32" role="img" aria-label={`${total} consultas en la ventana; máximo ${max} por hora`}>
          {buckets.map((b) => (
            <div key={b.hour} className={`flex-1 min-w-[3px] rounded-t ${b.failures > 0 ? 'bg-red-500' : 'bg-marzano-500 hover:bg-marzano-700'}`}
              style={{ height: `${Math.max(2, (b.count / max) * 100)}%` }}
              title={`${new Date(b.hour).toLocaleString('es-CL')}\n${b.count} consultas\nprom ${b.avg_latency_ms.toFixed(0)} ms · p95 ${b.p95_latency_ms.toFixed(0)} ms${b.failures > 0 ? `\n${b.failures} fallos` : ''}`} />
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-600"><span>{buckets[0] && fmt(buckets[0].hour)}</span><span>Rojo = hora con fallos</span><span>{buckets.length > 0 && fmt(buckets[buckets.length - 1]!.hour)}</span></div>
      </Body>
    </Card>
  );
}

function LatencyHistogramCard({ opts }: { opts: AuditQueryOpts }) {
  const { data, loading, error } = useAgg(() => api.getAuditLatencyHistogram(opts), [opts]);
  const histogram: AuditLatencyBucket[] = data?.histogram ?? [];
  const max = Math.max(1, ...histogram.map((h) => h.count));
  return (
    <Card title="Distribución de latencia">
      <Body loading={loading} error={error} empty={histogram.length === 0}>
        <ul className="space-y-1.5">
          {histogram.map((h) => {
            const slow = SLOW.includes(h.bucket);
            return (
              <li key={h.bucket} className="grid grid-cols-[88px_minmax(0,1fr)_56px] items-center gap-2">
                <span className={`text-[13px] font-mono ${slow ? 'text-amber-900 font-semibold' : 'text-slate-700'}`}>{h.bucket}{slow && ' ⚠'}</span>
                <span className="h-4 bg-slate-100 rounded"><span className={`block h-4 rounded ${slow ? 'bg-amber-500' : 'bg-marzano-500'}`} style={{ width: `${Math.max(1, (h.count / max) * 100)}%` }} /></span>
                <span className="text-[13px] text-right tabular-nums text-slate-800">{h.count.toLocaleString('es-CL')}</span>
              </li>
            );
          })}
        </ul>
      </Body>
    </Card>
  );
}

function TopUsersCard({ opts, onPickUser }: { opts: AuditQueryOpts; onPickUser: (id: string) => void }) {
  const { data, loading, error } = useAgg(() => api.getAuditTopUsers({ ...opts, limit: 10 }), [opts]);
  const users: AuditUserBucket[] = data?.users ?? [];
  return (
    <Card title="Usuarios con más consultas">
      <Body loading={loading} error={error} empty={users.length === 0}>
        <ol className="divide-y divide-slate-100">
          {users.map((u, i) => (
            <li key={u.user_mongo_id ?? `null-${i}`} className="py-2 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{u.user_email ?? u.user_mongo_id ?? '(sin identidad)'}</div>
                {u.active_role && <div className="text-xs text-slate-600">{u.active_role}</div>}
              </div>
              <div className="text-right shrink-0"><div className="text-sm font-semibold tabular-nums">{u.count.toLocaleString('es-CL')}</div><div className="text-xs text-slate-600 tabular-nums">{(u.total_latency_ms / 1000).toFixed(1)} s acum.</div></div>
              {u.user_mongo_id && <Button variant="ghost" size="sm" onClick={() => onPickUser(u.user_mongo_id ?? '')} aria-label={`Filtrar por ${u.user_email ?? u.user_mongo_id}`}>Filtrar</Button>}
            </li>
          ))}
        </ol>
      </Body>
    </Card>
  );
}

function TopNamespacesCard({ opts, onPickNamespace }: { opts: AuditQueryOpts; onPickNamespace: (ns: string) => void }) {
  const { data, loading, error } = useAgg(() => api.getAuditTopNamespaces({ ...opts, limit: 10 }), [opts]);
  const rows: AuditNamespaceBucket[] = data?.namespaces ?? [];
  return (
    <Card title="Colecciones y tablas más consultadas">
      <Body loading={loading} error={error} empty={rows.length === 0}>
        <ol className="divide-y divide-slate-100">
          {rows.map((ns) => (
            <li key={`${ns.db_type}:${ns.namespace}`} className="py-2 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[13px] text-slate-900 truncate">{ns.namespace}</div>
                <div className="text-xs text-slate-600 tabular-nums">{ns.db_type} · prom {ns.avg_latency_ms.toFixed(0)} ms · p95 {ns.p95_latency_ms.toFixed(0)} ms</div>
              </div>
              <div className="text-sm font-semibold tabular-nums shrink-0">{ns.count.toLocaleString('es-CL')}</div>
              <Button variant="ghost" size="sm" onClick={() => onPickNamespace(ns.namespace)} aria-label={`Filtrar por ${ns.namespace}`}>Filtrar</Button>
            </li>
          ))}
        </ol>
      </Body>
    </Card>
  );
}

function AuditRowsTable({ opts }: { opts: AuditQueryOpts }) {
  const [items, setItems] = useState<AuditRow[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async (reset: boolean) => {
    if (reset) setLoading(true); else setLoadingMore(true);
    setError(null);
    try {
      const r = await api.getAuditRows({ ...opts, limit: 50, ...(reset || !cursor ? {} : { cursor }) });
      setItems((prev) => (reset ? r.items : [...prev, ...r.items]));
      setCursor(r.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  }, [opts, cursor]);

  useEffect(() => { void load(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [opts]);

  return (
    <section aria-labelledby="rows-t" className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 id="rows-t" className="text-base font-semibold text-slate-900">Detalle de consultas</h2>
        <span className="text-[13px] text-slate-600 tabular-nums">{items.length} filas cargadas</span>
      </div>
      <Body loading={loading} error={error} empty={items.length === 0} emptyText="Sin registros para los filtros.">
        <div className={TABLE.wrap}>
          <table className={TABLE.table + ' text-[13px]'}>
            <thead className={TABLE.thead}>
              <tr>
                <th className="w-8" /><th className={TABLE.th}>Hora</th><th className={TABLE.th}>Usuario</th><th className={TABLE.th}>Endpoint</th>
                <th className={TABLE.th}>Base / colección</th><th className={TABLE.th}>Operación</th>
                <th className={TABLE.th + ' text-right'}>Latencia</th><th className={TABLE.th + ' text-right'}>Filas</th><th className={TABLE.th}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const isOpen = open === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr className={TABLE.tr + ' align-top'}>
                      <td className="pl-3 py-2.5">
                        {r.filter_snippet && (
                          <button type="button" aria-expanded={isOpen} aria-label="Ver filtro" onClick={() => setOpen(isOpen ? null : r.id)} className="p-0.5 rounded text-slate-500 hover:text-slate-900">
                            <ChevronRight aria-hidden className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-slate-700 whitespace-nowrap tabular-nums">
                        {new Date(r.occurred_at).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </td>
                      <td className="px-3 py-2.5 max-w-[200px]" title={r.user_mongo_id ?? undefined}>
                        <div className="truncate text-slate-900">{r.user_email ?? (r.user_mongo_id ? r.user_mongo_id.slice(0, 8) + '…' : '—')}</div>
                        {r.active_role && <div className="text-xs text-slate-600">{r.active_role}</div>}
                      </td>
                      <td className="px-3 py-2.5 max-w-[240px]">
                        {r.http_url ? <span className="font-mono"><span className="text-xs font-semibold text-slate-600">{r.http_method}</span> <span className="truncate inline-block max-w-[190px] align-bottom" title={r.http_url}>{r.http_url}</span></span> : <span className="text-slate-500">—</span>}
                      </td>
                      <td className="px-3 py-2.5 max-w-[220px]"><span className="text-xs text-slate-600">{r.db_type}</span><span className="font-mono block truncate" title={r.namespace}>{r.namespace}</span></td>
                      <td className="px-3 py-2.5 font-mono text-slate-800">{r.operation}</td>
                      <td className={'px-3 py-2.5 text-right font-mono whitespace-nowrap tabular-nums ' + (r.latency_ms >= 1000 ? 'text-amber-900 font-semibold' : r.latency_ms >= 200 ? 'text-amber-800' : 'text-slate-700')}>
                        {r.latency_ms >= 1000 && '⚠ '}{r.latency_ms.toLocaleString('es-CL')} ms
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">{r.rows_or_docs ?? '—'}</td>
                      <td className="px-3 py-2.5">{r.success ? <StatusBadge tone="ok">OK</StatusBadge> : <span title={r.error_code ?? undefined}><StatusBadge tone="danger">{r.error_code ?? 'Error'}</StatusBadge></span>}</td>
                    </tr>
                    {isOpen && r.filter_snippet && (
                      <tr className="bg-slate-50">
                        <td />
                        <td colSpan={8} className="px-3 py-2.5"><code className="block whitespace-pre-wrap break-all font-mono text-xs text-slate-800">{r.filter_snippet}</code></td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Body>
      {cursor && <div className="flex justify-center"><Button variant="secondary" size="sm" onClick={() => void load(false)} loading={loadingMore}>Cargar más</Button></div>}
    </section>
  );
}

function Body({ loading, error, empty, emptyText = 'Sin datos en la ventana.', children }: { loading: boolean; error: string | null; empty: boolean; emptyText?: string; children: React.ReactNode }) {
  if (loading) return <div className="flex justify-center py-6"><Loader2 aria-label="Cargando" className="w-5 h-5 animate-spin text-marzano-600" /></div>;
  if (error) return <Alert kind="error">{error}</Alert>;
  if (empty) return <p className="text-sm text-slate-600 text-center py-6">{emptyText}</p>;
  return <>{children}</>;
}
